import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../../hooks/useNavigate';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArticleIcon from '@mui/icons-material/Article';
import PageHeader from '../components/shared/page-header';
import { usePublicationDetailState } from './context/publication-detail-context';
import { usePublicationDetailController } from './context/publication-detail-controller';

export function PublicationDetailComponent() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { publicationDetail, loading, error } = usePublicationDetailState();
    const { loadDetail } = usePublicationDetailController();

    useEffect(() => {
        if (id) {
            loadDetail(parseInt(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const normalizeContent = (content?: string) => {
        if (!content) return '';

        // Remove caracteres de escape comuns
        let normalized = content
            .replace(/\\n/g, '\n')           // Converte \n literal em quebra de linha
            .replace(/\\t/g, ' ')            // Converte \t literal em espaço
            .replace(/\\r/g, '')             // Remove \r literal
            .replace(/\t/g, ' ')             // Converte tab em espaço
            .replace(/\r\n/g, '\n')          // Normaliza quebras de linha Windows
            .replace(/\r/g, '\n')            // Normaliza quebras de linha Mac
            .replace(/\n{3,}/g, '\n\n')      // Reduz múltiplas quebras de linha para no máximo 2
            .replace(/[ ]{2,}/g, ' ')        // Reduz múltiplos espaços para apenas 1
            .trim();                          // Remove espaços no início e fim

        return normalized;
    };

    const handleViewPublication = () => {
        if (publicationDetail?.fileViewUrl) {
            window.open(publicationDetail.fileViewUrl, '_blank');
        }
    };

    const handleDownloadPublication = () => {
        if (publicationDetail?.fileDownloadUrl) {
            window.open(publicationDetail.fileDownloadUrl, '_blank');
        }
    };

    const handleViewOfficialDaily = () => {
        if (publicationDetail?.dailyFileViewUrl) {
            window.open(publicationDetail.dailyFileViewUrl, '_blank');
        }
    };

    const handleDownloadOfficialDaily = () => {
        if (publicationDetail?.dailyFileDownloadUrl) {
            window.open(publicationDetail.dailyFileDownloadUrl, '_blank');
        }
    };

    if (loading) {
        return (
            <Box>
                <PageHeader
                    title="Publicação"
                    subtitle="Carregando detalhes..."
                    breadcrumbs={[
                        { label: 'Publicações do Diário Oficial', href: '/publicacoes' },
                        { label: 'Detalhes' }
                    ]}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (error || !publicationDetail) {
        return (
            <Box>
                <PageHeader
                    title="Publicação"
                    subtitle="Erro ao carregar detalhes"
                    breadcrumbs={[
                        { label: 'Publicações do Diário Oficial', href: '/publicacoes' },
                        { label: 'Detalhes' }
                    ]}
                />
                <Paper sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error || 'Não foi possível carregar os detalhes da publicação'}
                    </Alert>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/publicacoes')}
                    >
                        Voltar
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title={publicationDetail.description || 'Publicação'}
                subtitle={`Publicado em ${formatDate(publicationDetail.publicationDate)}`}
                breadcrumbs={[
                    { label: 'Publicações do Diário Oficial', href: '/publicacoes' },
                    { label: publicationDetail.description || 'Detalhes' }
                ]}
            />

            {/* Cabeçalho com informações principais */}
            <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <ArticleIcon color="primary" fontSize="large" />
                            <Typography variant="h5" component="h1" fontWeight={600}>
                                {publicationDetail.description}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip
                                icon={<CalendarTodayIcon />}
                                label={`Publicado em ${formatDate(publicationDetail.publicationDate)}`}
                                color="primary"
                                variant="outlined"
                            />
                            {publicationDetail.isRepublication && (
                                <Chip
                                    icon={<VerifiedIcon />}
                                    label="Republicação"
                                    color="warning"
                                    variant="outlined"
                                />
                            )}
                            {publicationDetail.category && (
                                <Chip
                                    icon={<CategoryIcon />}
                                    label={publicationDetail.category}
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                            {publicationDetail.subCategory && (
                                <Chip
                                    label={publicationDetail.subCategory}
                                    color="secondary"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            {publicationDetail.numberingCode && (
                                <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary">
                                        Código de Numeração
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {publicationDetail.numberingCode}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="text.secondary">
                                    Edição/Ano
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                    {publicationDetail.editionNumber}/{publicationDetail.year}
                                </Typography>
                            </Grid>
                            {publicationDetail.publicationAuthenticityCode && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Código de Autenticidade da Publicação
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                        {publicationDetail.publicationAuthenticityCode}
                                    </Typography>
                                </Grid>
                            )}
                            {publicationDetail.dailyAuthenticityCode && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Código de Autenticidade do Diário
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                        {publicationDetail.dailyAuthenticityCode}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/publicacoes')}
                                fullWidth
                            >
                                Voltar
                            </Button>
                            {publicationDetail.fileViewUrl && (
                                <Button
                                    variant="outlined"
                                    startIcon={<DescriptionIcon />}
                                    onClick={handleViewPublication}
                                    fullWidth
                                    color="primary"
                                >
                                    Visualizar Publicação
                                </Button>
                            )}
                            {publicationDetail.fileDownloadUrl && (
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadPublication}
                                    fullWidth
                                    color="primary"
                                >
                                    Baixar Publicação
                                </Button>
                            )}
                            {publicationDetail.dailyFileViewUrl && (
                                <Button
                                    variant="outlined"
                                    startIcon={<DescriptionIcon />}
                                    onClick={handleViewOfficialDaily}
                                    fullWidth
                                    color="secondary"
                                >
                                    Visualizar Diário Oficial Completo
                                </Button>
                            )}
                            {publicationDetail.dailyFileDownloadUrl && (
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadOfficialDaily}
                                    fullWidth
                                    color="secondary"
                                >
                                    Baixar Diário Oficial Completo
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Informações adicionais */}
            <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                    Informações Adicionais
                </Typography>

                <Grid container spacing={3}>
                    {publicationDetail.publishingVehicle && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <ArticleIcon color="primary" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Veículo de Publicação
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {publicationDetail.publishingVehicle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}



                    {publicationDetail.sanctionDate && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CalendarTodayIcon color="primary" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Data de Sanção
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {formatDate(publicationDetail.sanctionDate)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {publicationDetail.publishingAgency && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <BusinessIcon color="primary" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Órgão Publicador
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {publicationDetail.publishingAgency}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {publicationDetail.tenderCommission && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <GavelIcon color="primary" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Comissão de Licitação
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {publicationDetail.tenderCommission}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {publicationDetail.responsiblePresident && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <PersonIcon color="primary" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Presidente Responsável
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {publicationDetail.responsiblePresident}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {publicationDetail.observation && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Observação
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {normalizeContent(publicationDetail.observation)}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Box>
    );
}
